import type { Vec3 } from '../domain';
import type { IViewport } from '../viewports';
import { computeDeltaPoints, computeEventPoints } from './compute-event-points';
import type { IInteractionPoints, InteractionEvent, InteractionEventType } from './types';

/** Threshold in pixels — movement beyond this during a press means it's a drag, not a click. */
const DRAG_THRESHOLD_PX = 3;

/** Timeout in ms — if pointer is held longer than this without release, treat as drag intent. */
const CLICK_TIMEOUT_MS = 400;

/** Zero-value IInteractionPoints for keyboard events and delta initialization. */
const ZERO_POINTS: IInteractionPoints = {
    page: [0, 0],
    client: [0, 0],
    canvas: [0, 0],
    world: [0, 0, 0],
};

interface PointerTrackingState {
    pointerId: number;
    button: number;
    startTime: number;
    phase: 'pending' | 'dragging';
    timeoutId: ReturnType<typeof setTimeout> | null;
    startPoints: IInteractionPoints;
    lastPoints: IInteractionPoints;
    viewport: IViewport;
}

const LOG_STATE_CHANGES = false;
const LOG_PREFIX = '[EventStateMachine]';

function _logStateChange(message: string) {
    if (LOG_STATE_CHANGES) {
        console.log(`${LOG_PREFIX} ${message}`);
    }
}

function fmtPts(pts: IInteractionPoints): string {
    return `canvas=(${pts.canvas[0].toFixed(1)},${pts.canvas[1].toFixed(1)}) world=(${pts.world[0].toFixed(2)},${pts.world[1].toFixed(2)},${pts.world[2].toFixed(2)})`;
}

/**
 * State machine that distinguishes click from drag interactions.
 *
 * Accepts raw DOM events (PointerEvent | WheelEvent | KeyboardEvent) and
 * produces normalized InteractionEvent objects. Handles both:
 * - DOM → InteractionEvent normalization (replaces PointerEventNormalizer)
 * - Click/drag distinction via pointer tracking
 *
 * Produces:
 * - `pointer_down` — forwarded immediately so tools can initialize
 * - `mouse_click` — quick press+release (< 400ms, < 3px movement)
 * - `mouse_drag` — pointer moved while held, after threshold crossed
 * - `mouse_drag_end` — pointer released after a drag
 * - `pointer_move` — hover (no button pressed)
 * - `wheel` / `key_down` / `key_up` — passthrough
 *
 * Derived events are enriched with IInteractionPoints (startPoints, lastPoints,
 * currentPoints, deltaPoints) in page/client/canvas/world coordinate spaces.
 * World coordinates are recalculated on every event from the canvas position
 * using the viewport's current camera, so they stay correct even when the
 * camera has changed since the previous event.
 */
export class EventStateMachine {
    private pointerStates = new Map<number, PointerTrackingState>();

    /**
     * Process a raw DOM event, returning 0 or more derived InteractionEvents.
     */
    process(event: PointerEvent | WheelEvent | KeyboardEvent, viewport: IViewport): InteractionEvent | null {
        if (event instanceof PointerEvent) {
            return this._processPointerEvent(event, viewport);
        }

        if (event instanceof WheelEvent) {
            return this._buildWheelEvent(event, viewport);
        }

        if (event instanceof KeyboardEvent) {
            return this._buildKeyboardEvent(event, viewport);
        }

        return null;
    }

    reset(): void {
        for (const state of this.pointerStates.values()) {
            if (state.timeoutId !== null) {
                clearTimeout(state.timeoutId);
            }
        }
        this.pointerStates.clear();
    }

    dispose(): void {
        this.reset();
    }

    // ── Pointer dispatch ──

    private _processPointerEvent(event: PointerEvent, viewport: IViewport): InteractionEvent | null {
        const eventType = this._mapPointerEventType(event.type);

        switch (eventType) {
            case 'pointer_down':
                return this.onPointerDown(event, viewport);
            case 'pointer_move':
                return this.onPointerMove(event, viewport);
            case 'pointer_up':
                return this.onPointerUp(event, viewport);
            case 'pointer_leave':
                return this.onPointerLeave(event, viewport);
            default:
                return this._buildPointerEvent(event, eventType, viewport);
        }
    }

    // ── Handlers ──

    private onPointerDown(event: PointerEvent, viewport: IViewport): InteractionEvent {
        // Clean up any stale state for this pointer
        this.clearPointerState(event.pointerId);

        const startPoints = computeEventPoints(
            event.clientX,
            event.clientY,
            event.pageX,
            event.pageY,
            viewport
        );

        const state: PointerTrackingState = {
            pointerId: event.pointerId,
            button: event.button,
            startTime: event.timeStamp,
            phase: 'pending',
            timeoutId: null,
            startPoints: this._copyPoints(startPoints),
            lastPoints: this._copyPoints(startPoints),
            viewport,
        };

        // Start timeout — after this, treat any subsequent movement as drag
        state.timeoutId = setTimeout(() => {
            state.timeoutId = null;
            // If still pending when timeout fires, transition to dragging.
            // The next pointer_move will emit mouse_drag.
            if (state.phase === 'pending') {
                state.phase = 'dragging';
                _logStateChange(`pending→dragging (timeout ${CLICK_TIMEOUT_MS}ms) ptr=${state.pointerId}`);
            }
        }, CLICK_TIMEOUT_MS);

        this.pointerStates.set(event.pointerId, state);

        // Return normalized pointer_down event to tools so they can initialize
        const enrichedEvent = this._createEnrichedEvent(event, startPoints, 'pointer_down', state);
        _logStateChange(`pointer_down  ptr=${event.pointerId} btn=${event.button} ${fmtPts(startPoints)}`);
        return enrichedEvent;
    }

    private onPointerMove(event: PointerEvent, viewport: IViewport): InteractionEvent | null {
        const state = this.pointerStates.get(event.pointerId);

        // No tracked press — pass through as regular pointer_move (hover)
        if (!state) {
            return this._buildPointerEvent(event, 'pointer_move', viewport);
        }

        const currentPoints = computeEventPoints(
            event.clientX,
            event.clientY,
            event.pageX,
            event.pageY,
            state.viewport
        );

        if (state.phase === 'pending') {
            // Check if movement exceeds threshold
            const dx = currentPoints.canvas[0] - state.startPoints.canvas[0];
            const dy = currentPoints.canvas[1] - state.startPoints.canvas[1];
            const distance = Math.sqrt(dx * dx + dy * dy);

            if (distance > DRAG_THRESHOLD_PX) {
                // Crossed threshold → transition to dragging
                if (state.timeoutId !== null) {
                    clearTimeout(state.timeoutId);
                    state.timeoutId = null;
                }
                state.phase = 'dragging';

                _logStateChange(
                    `pending→dragging  ptr=${event.pointerId} distance=${distance.toFixed(1)}px ${fmtPts(currentPoints)}`
                );
                const enriched = this._createEnrichedEvent(event, currentPoints, 'mouse_drag', state);
                state.lastPoints = this._copyPoints(currentPoints);
                return enriched;
            }

            // Still within threshold — suppress the move event
            return null;
        }

        if (state.phase === 'dragging') {
            const enriched = this._createEnrichedEvent(event, currentPoints, 'mouse_drag', state);
            state.lastPoints = this._copyPoints(currentPoints);
            _logStateChange(
                `mouse_drag    ptr=${event.pointerId} ${fmtPts(currentPoints)} delta_canvas=(${enriched.deltaPoints.canvas[0].toFixed(1)},${enriched.deltaPoints.canvas[1].toFixed(1)}) delta_world=(${enriched.deltaPoints.world[0].toFixed(2)},${enriched.deltaPoints.world[1].toFixed(2)},${enriched.deltaPoints.world[2].toFixed(2)})`
            );
            return enriched;
        }

        return null;
    }

    private onPointerUp(event: PointerEvent, viewport: IViewport): InteractionEvent | null {
        const state = this.pointerStates.get(event.pointerId);

        if (!state) {
            // No tracked press — pass through
            return this._buildPointerEvent(event, 'pointer_up', viewport);
        }

        const currentPoints = computeEventPoints(
            event.clientX,
            event.clientY,
            event.pageX,
            event.pageY,
            state.viewport
        );

        const phase = state.phase;

        if (phase === 'pending') {
            // Quick release before threshold/timeout → it's a click
            const enriched = this._createEnrichedEvent(event, currentPoints, 'mouse_click', state);
            const holdMs = event.timeStamp - state.startTime;
            _logStateChange(
                `mouse_click   ptr=${event.pointerId} btn=${state.button} held=${holdMs.toFixed(0)}ms ${fmtPts(currentPoints)}`
            );
            this.clearPointerState(event.pointerId);
            return enriched;
        }

        if (phase === 'dragging') {
            // Release after drag → drag end
            const enriched = this._createEnrichedEvent(event, currentPoints, 'mouse_drag_end', state);
            const holdMs = event.timeStamp - state.startTime;
            _logStateChange(
                `mouse_drag_end ptr=${event.pointerId} btn=${state.button} held=${holdMs.toFixed(0)}ms ${fmtPts(currentPoints)}`
            );
            this.clearPointerState(event.pointerId);
            return enriched;
        }

        this.clearPointerState(event.pointerId);
        return this._buildPointerEvent(event, 'pointer_up', viewport);
    }

    private onPointerLeave(event: PointerEvent, viewport: IViewport): InteractionEvent | null {
        const state = this.pointerStates.get(event.pointerId);

        if (!state) {
            return this._buildPointerEvent(event, 'pointer_leave', viewport);
        }

        const phase = state.phase;

        // If dragging and pointer leaves, emit drag_end to clean up tool state
        if (phase === 'dragging') {
            const currentPoints = computeEventPoints(
                event.clientX,
                event.clientY,
                event.pageX,
                event.pageY,
                state.viewport
            );
            const enriched = this._createEnrichedEvent(event, currentPoints, 'mouse_drag_end', state);
            _logStateChange(
                `mouse_drag_end (leave) ptr=${event.pointerId} btn=${state.button} ${fmtPts(currentPoints)}`
            );
            this.clearPointerState(event.pointerId);
            return enriched;
        }

        // If pending and pointer leaves, cancel the interaction
        _logStateChange(`pointer_leave (pending cancelled) ptr=${event.pointerId}`);
        this.clearPointerState(event.pointerId);
        return null;
    }

    // ── Helpers ──

    private clearPointerState(pointerId: number): void {
        const state = this.pointerStates.get(pointerId);
        if (state) {
            if (state.timeoutId !== null) {
                clearTimeout(state.timeoutId);
            }
            this.pointerStates.delete(pointerId);
        }
    }

    /**
     * Create a derived event enriched with IInteractionPoints.
     *
     * World coordinates for startPoints and lastPoints are recalculated from
     * their stored canvas positions using the viewport's current camera. This
     * is critical because a previous tool event may have moved the camera,
     * making the old world coordinates stale.
     */
    private _createEnrichedEvent(
        source: PointerEvent,
        currentPoints: IInteractionPoints,
        type: InteractionEventType,
        state: PointerTrackingState
    ): InteractionEvent {
        const viewport = state.viewport;

        // Recalculate last points with the current camera
        const lastPoints = computeEventPoints(
            state.lastPoints.client[0],
            state.lastPoints.client[1],
            state.lastPoints.page[0],
            state.lastPoints.page[1],
            viewport
        );

        const deltaPoints = computeDeltaPoints(currentPoints, lastPoints);

        return {
            type,
            timestamp: source.timeStamp,

            pointerId: source.pointerId,
            pointerType: source.pointerType as 'mouse' | 'touch' | 'pen',
            isPrimary: source.isPrimary,

            shiftKey: source.shiftKey,
            ctrlKey: source.ctrlKey,
            altKey: source.altKey,
            metaKey: source.metaKey,

            button: state.button,
            buttons: source.buttons,
            startPoints: this._copyPoints(state.startPoints),
            lastPoints,
            currentPoints,
            deltaPoints,

            viewport,
            originalEvent: source,
            consumed: false,
            consume() {
                this.consumed = true;
                source.preventDefault();
                source.stopPropagation();
            },
        };
    }

    /**
     * Build a simple InteractionEvent from a raw PointerEvent (no tracking state).
     * Used for hover moves, untracked pointer_up/leave, and other passthrough cases.
     */
    private _buildPointerEvent(
        event: PointerEvent,
        type: InteractionEventType,
        viewport: IViewport
    ): InteractionEvent {
        const points = computeEventPoints(event.clientX, event.clientY, event.pageX, event.pageY, viewport);

        return {
            type,
            timestamp: event.timeStamp,

            pointerId: event.pointerId,
            pointerType: event.pointerType as 'mouse' | 'touch' | 'pen',
            isPrimary: event.isPrimary,

            shiftKey: event.shiftKey,
            ctrlKey: event.ctrlKey,
            altKey: event.altKey,
            metaKey: event.metaKey,

            button: event.button,
            buttons: event.buttons,

            startPoints: points,
            lastPoints: points,
            currentPoints: points,
            deltaPoints: ZERO_POINTS,

            viewport,
            originalEvent: event,
            consumed: false,
            consume() {
                this.consumed = true;
                event.preventDefault();
                event.stopPropagation();
            },
        };
    }

    private _buildWheelEvent(event: WheelEvent, viewport: IViewport): InteractionEvent {
        const points = computeEventPoints(event.clientX, event.clientY, event.pageX, event.pageY, viewport);

        _logStateChange(
            `wheel         deltaY=${event.deltaY.toFixed(1)} mode=${event.deltaMode} ${fmtPts(points)}`
        );

        return {
            type: 'wheel',
            timestamp: event.timeStamp,

            pointerId: 0,
            pointerType: 'mouse',
            isPrimary: true,

            shiftKey: event.shiftKey,
            ctrlKey: event.ctrlKey,
            altKey: event.altKey,
            metaKey: event.metaKey,

            button: -1,
            buttons: 0,

            startPoints: points,
            lastPoints: points,
            currentPoints: points,
            deltaPoints: ZERO_POINTS,

            deltaX: event.deltaX,
            deltaY: event.deltaY,
            deltaMode: event.deltaMode,

            viewport,
            originalEvent: event,
            consumed: false,
            consume() {
                this.consumed = true;
                event.preventDefault();
                event.stopPropagation();
            },
        };
    }

    private _buildKeyboardEvent(event: KeyboardEvent, viewport: IViewport): InteractionEvent {
        const type = event.type === 'keydown' ? 'key_down' : 'key_up';
        _logStateChange(
            `${type.padEnd(14)} key="${event.key}" code=${event.code}${event.repeat ? ' (repeat)' : ''}`
        );

        return {
            type,
            timestamp: event.timeStamp,

            pointerId: -1,
            pointerType: 'mouse',
            isPrimary: true,

            shiftKey: event.shiftKey,
            ctrlKey: event.ctrlKey,
            altKey: event.altKey,
            metaKey: event.metaKey,

            button: -1,
            buttons: 0,

            startPoints: ZERO_POINTS,
            lastPoints: ZERO_POINTS,
            currentPoints: ZERO_POINTS,
            deltaPoints: ZERO_POINTS,

            key: event.key,
            code: event.code,
            repeat: event.repeat,

            viewport,
            originalEvent: event,
            consumed: false,
            consume() {
                this.consumed = true;
                event.preventDefault();
                event.stopPropagation();
            },
        };
    }

    private _copyPoints(points: IInteractionPoints): IInteractionPoints {
        return {
            page: [...points.page] as [number, number],
            client: [...points.client] as [number, number],
            canvas: [...points.canvas] as [number, number],
            world: [...points.world] as Vec3,
        };
    }

    private _mapPointerEventType(domType: string): InteractionEventType {
        switch (domType) {
            case 'pointerdown':
                return 'pointer_down';
            case 'pointermove':
                return 'pointer_move';
            case 'pointerup':
                return 'pointer_up';
            case 'pointercancel':
                return 'pointer_cancel';
            case 'pointerenter':
                return 'pointer_enter';
            case 'pointerleave':
                return 'pointer_leave';
            default:
                throw new Error(`Unsupported PointerEvent type: ${domType}`);
        }
    }
}
